import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FooterSection from '../landing-page/components/FooterSection';
import BusinessHeroSection from './components/BusinessHeroSection';
import BusinessInterviewProductSection from './components/BusinessInterviewProductSection';
import BusinessTaglineMarquee from './components/BusinessTaglineMarquee';
import BusinessMetricsSection from './components/BusinessMetricsSection';
import BusinessPipelineSection from './components/BusinessPipelineSection';
import BusinessRolesSection from './components/BusinessRolesSection';
import BusinessLearningSection from './components/BusinessLearningSection';
import BusinessCTASection from './components/BusinessCTASection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { businessSignupUrl, businessLoginUrl } from './businessApp';
import '../landing-page/landing.css';

const navLinks = [
  { href: '/', label: 'Home', isRoute: true },
  { href: '#interview-ide', label: 'Interview IDE', isRoute: false },
  { href: '#pipeline', label: 'Pipeline', isRoute: false },
  { href: '#contact', label: 'Contact', isRoute: false },
];

// NOTE: this page is a pure marketing funnel for the business hiring product.
// Business auth lives in a separate console (the /business/* SPA) with its own
// trust boundary, so we do NOT use the consumer auth modal here, and we do NOT
// redirect authenticated consumer users away — a logged-in student may still be
// evaluating this for their employer. All CTAs deep-link into the business SPA.
const ForBusinessesPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      setScrolled(window.scrollY > 24);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggleTheme = () => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  return (
    <div className="landing-page for-business-page min-h-screen bg-white text-[var(--lp-ink)] transition-colors duration-300">
      <header
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          scrolled ? 'border-b border-[var(--lp-line)] bg-white/90 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
          <Link to="/" className="text-xl font-semibold tracking-tight text-[var(--lp-ink)]">
            prepx<span className="text-[var(--lp-accent)]">ai</span> <span className="text-sm font-normal text-[var(--lp-ink-soft)] hidden sm:inline">for Business</span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-mono text-[11px] font-semibold tracking-wider text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-mono text-[11px] font-semibold tracking-wider text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              aria-label="Toggle theme"
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
            </button>

            <a
              href={businessLoginUrl()}
              className="hidden text-sm font-medium text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)] sm:inline-flex"
            >
              Sign in
            </a>
            <a
              href={businessSignupUrl()}
              className="hidden h-9 items-center rounded-full bg-[var(--lp-accent)] px-5 text-xs text-white transition-colors hover:bg-[var(--lp-accent)]/90 sm:inline-flex"
            >
              Get started
            </a>

            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] md:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Menu"
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={18} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-[var(--lp-line)] bg-white px-5 py-4 md:hidden">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
                  >
                    {link.label}
                  </a>
                )
              ))}
              <a
                href={businessLoginUrl()}
                onClick={() => setMobileOpen(false)}
                className="mt-1 font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
              >
                Sign in
              </a>
              <a
                href={businessSignupUrl()}
                onClick={() => setMobileOpen(false)}
                className="mt-2 flex h-10 items-center justify-center rounded-full bg-[var(--lp-accent)] text-sm text-white hover:bg-[var(--lp-accent)]/90"
              >
                Get started
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="pt-16">
        <BusinessHeroSection />
        <BusinessInterviewProductSection />
        <BusinessTaglineMarquee />
        <BusinessMetricsSection />
        <BusinessPipelineSection />
        <BusinessRolesSection />
        <BusinessLearningSection />
        <BusinessCTASection />
        <FooterSection />
      </main>

      {showScrollTop && (
        <Button
          variant="default"
          size="icon"
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-[90] h-10 w-10 rounded-full border border-[var(--lp-line)] bg-white text-[var(--lp-ink)]"
          aria-label="Scroll to top"
        >
          <Icon name="ArrowUp" size={18} />
        </Button>
      )}
    </div>
  );
};

export default ForBusinessesPage;
